import { Tooltip } from "flowbite-react";
import React, { useState } from "react";
import toast from "react-hot-toast";
import { FaGlasses } from "react-icons/fa";
import { HiOutlineInformationCircle } from "react-icons/hi2";
import { useNavigate } from "react-router";
import CustomTooltip from "../../../../components/common/CustomTooltip";
import { getDashboardStatusLabel, stageBgColorMap, stageTextColorMap } from "../../../../constants/integrationTypes";
import { useResourceInfo } from "../../../../hooks/useResourceInfo";
import Resources from "../../../../pages/Projects/sections/Project/sections/ProjectBoxComponent/Resources";
import WorkItemOptions from "../../../../pages/Projects/sections/Project/sections/WorkItemOptions";
import CadenceDataTable from "../../../../shared/molecules/CadenceDataTable/CadenceDataTable";
import { PriorityCell, ProgressCell, StatusCell, TitleCell } from "../../../../shared/molecules/CadenceTable/CadenceTable";
import { newPath } from "../../../../utils/newPath";
import { smapNavigationLinkBuilder } from "../../../../utils/strategyMapUtils";
import * as def from "../../StrategyMapView.def";

interface StrategyMapTableViewProps {
  state: def.StrategyMapViewStateProps;
}

function formatShortDate(dateStr: string) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleString("en-US", { month: "short", day: "numeric" });
}



export function StrategyMapTableView({ state }: StrategyMapTableViewProps) {
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const { getResourceName } = useResourceInfo();

  const simulation = state.get.simulation;
  const table_data = state.get.strageyMapData?.entities || [];

  const handleCopyId = async (id: string, event: React.MouseEvent) => {
    event.stopPropagation();
    try {
      await navigator.clipboard.writeText(id);
      setCopiedId(id);
      toast.success("ID copied to clipboard!");
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error("Failed to copy ID:", err);
      toast.error("Failed to copy ID");
    }
  };

  const navigate = useNavigate()

  const hasIntegrationType = Array.isArray(table_data) && table_data.some((item) => item.integration_data);

  const columns = [
    {
      key: "name",
      label: "Title",
      render: (task: any) => <TitleCell row={task} />,
    },
    {
      key: "status",
      label: "Status",
      searchValue: (item: any) =>
        getDashboardStatusLabel(
          (simulation ? item?.sim_data?.status : item?.status) || "",
        ),
      render: (item: any) => {
        return <StatusCell row={item} />;
      },
    },
    {
      key: "progress",
      label: "Progress",
      searchValue: (item: any) =>
        simulation ? (item?.sim_data?.progress ?? "") : (item?.progress ?? ""),
      render: (item: any) => {
        const progress = simulation ? item?.sim_data?.progress : item?.progress;
        return (
          <div className="flex justify-start">
            <ProgressCell progress={progress} />
          </div>
        );
      },
      sortValue: (row: any) =>
        simulation ? (row?.sim_data?.progress ?? 0) : (row?.progress ?? 0),
    },
    ...(hasIntegrationType
      ? [
          {
            key: "integration_type",
            label: "Integration Type",
            searchValue: (item: any) =>
              item.integration_data?.integration_item_type,
            render: (item: any) => {
              const type = item.integration_data?.integration_item_type;
              return type ? (
                <span className="capitalize text-xs font-medium px-2.5 py-1 rounded bg-purple-100 text-purple-800 dark:bg-purple-200 dark:text-purple-900">
                  {type}
                </span>
              ) : (
                <span>-</span>
              );
            },
            sortValue: (item: any) =>
              (
                item.integration_data?.integration_item_type || ""
              ).toLowerCase(),
          },
        ]
      : []),
    {
      key: "stage",
      label: "Stage",
      searchValue: (item: any) => item.stage,
      render: (item: any) => {
        const stage = (item.stage || "").toLowerCase();
        const bgClass =
          stageBgColorMap[stage] || "bg-gray-100 dark:bg-gray-800";
        const textClass =
          stageTextColorMap[stage] || "text-gray-700 dark:text-gray-200";
        return (
          <span
            className={`${bgClass} ${textClass} text-xs font-medium px-2.5 py-1 rounded`}
          >
            {item.stage || ""}
          </span>
        );
      },
    },
  {
      key: "priority",
      label: "Priority",
      searchValue: (task: any) => task?.priority || "",
      render: (task: any) => <PriorityCell priority={task?.priority} />,
      sortValue: (task: any) => task?.priority || "",
    },
    {
      key: "team_members",
      label: "Team Members",
      searchValue: (item: any) => {
        const resourceIds = Array.isArray(item.members)
          ? item.members.map((m: any) =>
              typeof m === "object" && m !== null ? m.id : m,
            )
          : [];
        const names = resourceIds
          .map((id: any) => getResourceName(id))
          .filter(Boolean);
        return names.join(", ");
      },
      render: (item: any) => {
        const resourceIds = Array.isArray(item.members)
          ? item.members.map((m: any) =>
              typeof m === "object" && m !== null ? m.id : m,
            )
          : [];
        return <Resources resources={resourceIds} projectId={item.id} />;
      },
    },
    {
      key: "start_end",
      label: "Start & end date",
      searchValue: (item: any) => {
        const start = simulation
          ? item?.sim_data?.start_date
          : item?.start_date;
        const end = simulation ? item?.sim_data?.end_date : item?.end_date;
        const shortStart = formatShortDate(start);
        const shortEnd = formatShortDate(end);
        const locStart = start ? new Date(start).toLocaleDateString() : "";
        const locEnd = end ? new Date(end).toLocaleDateString() : "";
        return [
          start || "",
          end || "",
          shortStart || "",
          shortEnd || "",
          locStart,
          locEnd,
        ]
          .filter(Boolean)
          .join(" ");
      },
      sortValue: (row: any) => {
        if (simulation) {
          return row?.sim_data?.start_date
            ? new Date(row?.sim_data?.start_date).getTime()
            : 0;
        }
        return row?.start_date ? new Date(row?.start_date).getTime() : 0;
      },
      render: (item: any) => {
        const start = simulation
          ? item?.sim_data?.start_date
          : item?.start_date;
        const end = simulation ? item?.sim_data?.end_date : item?.end_date;

        if (start && end) {
          const shortStart = formatShortDate(start);
          const shortEnd = formatShortDate(end);
          return (
            <span
              title={`${start} - ${end}`}
              className="max-w-[120px] truncate block"
            >
              {shortStart} - {shortEnd}
            </span>
          );
        }

        return <span>-</span>;
      },
    },
    {
      key: "id",
      label: "ID",
      searchValue: (item: any) => item.id,
      headerRender: () => (
        <span className="flex items-center gap-1">
          <Tooltip
            className="bg-white dark:bg-gray-800"
            onClick={(e) => e.stopPropagation()}
            content={
              <div className="flex flex-col gap-1">
                <span className="lowercase text-gray-500 dark:text-gray-400">
                  Unique identifier
                </span>
                <span className=" lowercase text-gray-500 dark:text-gray-400">
                  {" "}
                  (click to copy)
                </span>
              </div>
            }
            placement="top"
          >
            <span className="ml-1 text-gray-400">
              <HiOutlineInformationCircle className="inline-block w-4 h-4 align-middle" />
            </span>
          </Tooltip>
          ID
        </span>
      ),
      render: (item: any) => (
        <button
          onClick={(e) => handleCopyId(item.id, e)}
          className="truncate w-full flex justify-center text-left hover:underline hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors duration-200 cursor-pointer relative group"
          title={`Click to copy: ${item.id}`}
        >
          <span
            className={`transition-colors duration-200 ${copiedId === item.id ? "text-green-600 dark:text-green-400" : ""}`}
          >
            {item.id}
          </span>
        </button>
      ),
    },
    {
      key: "action",
      label: "",
      sortable: false,
      skeletonShape: "action",
      render: (item: any) => {
        return (
          <div className="flex items-center justify-end gap-0">
            {/* Sim Pending Flag - glasses icon matching card view */}
            {item.is_sim_pending && (
              <CustomTooltip title="Simulation present">
                <div className="-mr-3 w-6 h-8 bg-[#467966] rounded-bl-2xl rounded-br-2xl flex items-center justify-center pt-1 cursor-help">
                  <FaGlasses className="text-white w-3 h-3" />
                </div>
              </CustomTooltip>
            )}
            <WorkItemOptions
              data={item}
              className=""
              showOnHover={false}
              hideCopy={true}
              onRefresh={()=>state.set(pv=>({...pv, refreshKey: pv.refreshKey+1}))}
              hideEdit={false}
              onViewDetails={() => {
                const currentUrl = location.pathname + location.search;
                const newHistory = [
                  { name: "Strategy Map", url: "/strategymaps" },
                     {
                       name: state.get.strageyMapData?.name,
                       url: currentUrl,
                     },
                   ];
                   const path =
                     item.type == "roadmap" ? `/roadmaps/${item.id}` : `/projects/${item.id}`;

                   navigate(
                     newPath(path, { history: JSON.stringify(newHistory) }),
                   );
              }}
            />
          </div>
        );
      },
    },
  ];

  const defaultVisible = columns.reduce((acc, col) => {
    acc[col.key] = col.key !== "id";
    return acc;
  }, {} as Record<string, boolean>);

  return (
    <div className="flex flex-col h-full bg-tertiary-light dark:bg-tertiary-dark overflow-hidden px-4 pb-2">
      <div className="flex-1 overflow-hidden pb-[50px]">
        <CadenceDataTable
          columns={columns}
          data={table_data}
          loading={state.get.loadingSingleStrategyMap}
          onRowClick={({ type, id }: any) => {
            navigate(
              smapNavigationLinkBuilder(state.get.strageyMapData?.name ?? "", {
                type,
                id,
              }),
            );

            // Handle row click if needed, or leave it for now
          }}
          defaultVisible={defaultVisible}
          columnVisibilityKey={`strategy_map_table`}
          columnWeights={{
            name: 22,
            status: 10,
            progress: 8,
            integration_type: 10,
            stage: 8,
            team_members: 10,
            start_end: 12,
            id: 8,
            action: 8,
          }}
          emptyMessage={
            <div className="dark:text-white text-gray-800 flex justify-center items-center h-[50vh]">
              No data found.
            </div>
          }
        />
      </div>
    </div>
  );
}
